/**
 * Action Status Polling Hook
 *
 * Uses TanStack Query's refetchInterval to poll lead statuses for
 * all running actions. Detects completion when all leadIds for an
 * action have reached the targetStatus.
 *
 * Uses refs for store values to avoid restarting the effect on every render.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useRunningActions, useActionTrackerActions } from '../stores/actionTracker.store';
import { fetchLeadsFromN8N } from '../utils/n8nDataFetchers';
import { seedCampaignLeads } from '../services/query/seedCampaignLeads';

const POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutes — enrichment/outreach takes 5-10 min
const MAX_DURATION = 15 * 60 * 1000; // 15 minutes

export const useActionStatusPolling = (userEmail?: string) => {
  const queryClient = useQueryClient();
  const storeActions = useActionTrackerActions();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Use refs to avoid re-starting the effect when action array identity changes
  const runningActions = useRunningActions();
  const actionsRef = useRef(runningActions);
  actionsRef.current = runningActions;
  const storeActionsRef = useRef(storeActions);
  storeActionsRef.current = storeActions;

  useEffect(() => {
    // Don't poll when there are no actions to track — prevents the 5-minute
    // interval from running forever after a single enrichment/outreach completes.
    // Old 'timeout' actions from previous sessions do NOT count as running.
    if (!userEmail || runningActions.length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const checkActions = async () => {
      const currentActions = actionsRef.current;
      if (currentActions.length === 0) return;

      const now = Date.now();

      // Group running actions by campaignId for batch fetching
      const byCampaign = new Map<string, typeof currentActions>();
      for (const action of currentActions) {
        const existing = byCampaign.get(action.campaignId) || [];
        existing.push(action);
        byCampaign.set(action.campaignId, existing);
      }

      for (const [campaignId, actions] of byCampaign) {
        try {
          const leads = await fetchLeadsFromN8N(campaignId, userEmail);

          // Dual-variant seed via the shared write path — this previously
          // wrote only the email-variant key, leaving bare-key readers stale.
          seedCampaignLeads(queryClient, campaignId, userEmail, leads);

          for (const action of actions) {
            if (now - action.startedAt > MAX_DURATION) {
              storeActionsRef.current.timeoutAction(action.id);
              continue;
            }

            const leadIdSet = new Set(action.leadIds.map(String));
            const relevantLeads = leads.filter((lead: any) =>
              leadIdSet.has(String(lead.id))
            );

            if (relevantLeads.length === 0) continue;

            const allReachedTarget = relevantLeads.every((lead: any) => {
              const status = String(lead.status || '').toLowerCase();
              return status === action.targetStatus.toLowerCase();
            });

            if (allReachedTarget) {
              storeActionsRef.current.completeAction(action.id);
            }
          }
        } catch (error) {
          console.warn(`[ActionTracker] Failed to poll campaign ${campaignId}:`, error);
        }
      }
    };

    intervalRef.current = setInterval(checkActions, POLLING_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [userEmail, queryClient]);
};
